<template>
  <div id="top" class="auth-add-page">
    <div class="auth-add-intro">
      <UiPageHeader
        title="Add authorization"
        description="Find an active enrollee and prepare a new authorization request."
      >
        <template #actions>
          <button type="button" class="auth-add-return" @click.prevent="handleActive(home)">
            <ArrowLeft :size="17" aria-hidden="true" />
            <span>All Authorizations</span>
          </button>
        </template>
      </UiPageHeader>
    </div>

    <div class="auth-add-workbench sba-slide-in" :key="enrollee ? 'auth-add-detail' : 'auth-add-start'">
      <div class="auth-add-content">
        <LoadingDiv v-if="!isReady" />

        <template v-else>
          <EnrolleeAuth @restart="handleRestart" :enrollee="enrollee" :serverDate="serverDate" v-if="enrollee" />
          <StartAuth @next="handleNext" v-else />
        </template>
      </div>
    </div>
  </div>
</template>


<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { ArrowLeft } from "@lucide/vue";
import useModuleNavigation from "@/composables/useModuleNavigation";
import LoadingDiv from "@/components/LoadingDiv.vue";
import StartAuth from "@/components/auth/StartAuthComponent.vue";
import EnrolleeAuth from "@/components/auth/EnrolleeAuthComponent.vue";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";

export default {
  name: "authAdd",
  components: { ArrowLeft, EnrolleeAuth, LoadingDiv, StartAuth, UiPageHeader },
  setup() {
    const back = ref("auth"); // default
    const isReady = ref(true);
    const enrollee = ref(null);
    const serverDate = ref(null);
    const home = ref(null);

    const { routeToTab } = useModuleNavigation();

    // start
    onMounted(() => {
      // page name
      document.title = "Add Authorization";
      document.documentElement.classList.add("auth-add-active");
      nextTick(() => window.scrollTo({ top: 0, behavior: "auto" }));
    });

    onBeforeUnmount(() => {
      document.documentElement.classList.remove("auth-add-active");
    });

    // menu
    onMounted(() => {
      // load tab
      home.value = { id: 1, name: "Pre-Auth", title: "All", icon: "arrow_back", url: "auth", hash: null };
    });

    const scrollToPageTop = () => {
      nextTick(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    };

    // handle curr status
    const handleActive = (tab) => {
      if (!tab) {
        return;
      }

      if (tab.url === "authAdd") {
        handleRestart();
        document.title = "Add Authorization";
        scrollToPageTop();
        routeToTab(tab, { replace: true, scroll: false });
        return;
      }

      if (tab.url === "auth") {
        handleRestart();
        routeToTab(tab);
        return;
      }

      routeToTab(tab);
    };

    // // handle back
    // const handleBack = () => {
    //   if (back.value == "auth") {
    //     router.push({ name: "auth" }); // final back
    //   } else if (back.value == "authAdd") {
    //     handleRestart(); // restart auth
    //   } else {
    //     router.push({ name: "home" }); // safeguard to prevent unexpected errors
    //   }
    // };

    // handle next
    const handleNext = (val) => {
      enrollee.value = val.value;
      serverDate.value = val.serverDate;
      back.value = "authAdd";
    };

    // handle restart
    const handleRestart = () => {
      enrollee.value = null;
      serverDate.value = null;
      back.value = "auth";
      scrollToPageTop();
    };


    return {
      handleActive,
      home,
      serverDate,
      handleNext,
      handleRestart,
      isReady,
      enrollee,
    };
  },
};
</script>

<style scoped>
:global(html.auth-add-active),
:global(html.auth-add-active body),
:global(html.auth-add-active #app),
:global(html.auth-add-active .app-shell main) {
  height: auto !important;
  min-height: 0 !important;
}

:global(html.auth-add-active .app-shell) {
  height: auto !important;
  min-height: 100vh !important;
}

.auth-add-page {
  background: var(--ui-background);
  box-sizing: border-box;
  margin: 0 auto;
  max-width: var(--ui-content-width);
  padding: 2rem 2rem 3rem;
  width: 100%;
}

.auth-add-intro {
  border-bottom: 1px solid var(--ui-border);
  margin-bottom: 2rem;
  padding-bottom: 1.75rem;
}

.auth-add-intro :deep(.ui-page-header) {
  margin-bottom: 0;
}

.auth-add-intro :deep(.ui-page-header__title) {
  font-size: clamp(1.75rem, 2.1vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.025em;
}

.auth-add-intro :deep(.ui-page-header__description) {
  font-size: 1rem;
  line-height: 1.55;
}

.auth-add-return {
  align-items: center;
  background: var(--ui-primary);
  border: 1px solid var(--ui-primary);
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: none;
  color: var(--ui-primary-foreground);
  cursor: pointer;
  display: inline-flex;
  font-family: var(--ui-font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  gap: 0.5rem;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0 0.9rem;
}

.auth-add-return:hover {
  filter: brightness(0.95);
}

.auth-add-return:focus-visible {
  outline: 2px solid var(--ui-ring);
  outline-offset: 2px;
}

.auth-add-workbench,
.auth-add-content {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  min-width: 0;
  overflow: visible;
  padding: 0 !important;
  width: 100%;
}

.auth-add-content,
.auth-add-workbench :deep(.auth-start-shell),
.auth-add-workbench :deep(.auth-enrollee-card),
.auth-add-workbench :deep(.auth-provider-workflow) {
  animation: sbaSlideIn var(--sba-slide-duration, 300ms) cubic-bezier(0.22, 1, 0.36, 1) both;
}

@media (max-width: 600px) {
  .auth-add-page {
    padding: 1.25rem 1rem 2rem;
  }

  .auth-add-intro {
    margin-bottom: 1.5rem;
    padding-bottom: 1.25rem;
  }

  .auth-add-return {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-add-content,
  .auth-add-workbench :deep(.auth-start-shell),
  .auth-add-workbench :deep(.auth-enrollee-card),
  .auth-add-workbench :deep(.auth-provider-workflow) {
    animation: none;
  }
}
</style>
